import React, { useState } from 'react'
import styled from 'styled-components'
import moment from 'moment'
import HistoryIcon from '@mui/icons-material/History'
import PersonOutlineIcon from '@mui/icons-material/PersonOutline'
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import SendOutlinedIcon from '@mui/icons-material/SendOutlined'
import AutoFixHighOutlinedIcon from '@mui/icons-material/AutoFixHighOutlined'
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { maskEmail } from 'utils/string'

const EVENT_CONFIG = {
  auto_populate: {
    label: 'Auto-populated',
    color: '#6366f1',
    bg: '#eef2ff',
    Icon: AutoFixHighOutlinedIcon,
  },
  draft_save: {
    label: 'Draft Saved',
    color: '#0891b2',
    bg: '#ecfeff',
    Icon: EditOutlinedIcon,
  },
  submission: {
    label: 'Submitted',
    color: '#059669',
    bg: '#ecfdf5',
    Icon: SendOutlinedIcon,
  },
  approver_edit: {
    label: 'Approver Edit',
    color: '#7c3aed',
    bg: '#f5f3ff',
    Icon: EditOutlinedIcon,
  },
}

const ROLE_CONFIG = {
  system: { label: 'System', Icon: SmartToyOutlinedIcon },
  assignee: { label: 'Assignee', Icon: PersonOutlineIcon },
  approver: { label: 'Approver', Icon: PersonOutlineIcon },
  external: { label: 'External', Icon: PublicOutlinedIcon },
}

const FormVersionHistory = ({ versions, loading }) => {
  const [expandedVersion, setExpandedVersion] = useState(null)

  if (loading) {
    return (
      <Container>
        <LoadingText>Loading version history...</LoadingText>
      </Container>
    )
  }

  if (!versions || versions.length === 0) {
    return (
      <Container>
        <EmptyText>No version history available</EmptyText>
      </Container>
    )
  }

  const toggleExpanded = (versionId) => {
    setExpandedVersion(prev => prev === versionId ? null : versionId)
  }

  return (
    <Container>
      <Timeline>
        {versions.map((version, index) => {
          const eventConfig = EVENT_CONFIG[version.eventType] || EVENT_CONFIG.draft_save
          const roleConfig = ROLE_CONFIG[version.authorRole] || ROLE_CONFIG.assignee
          const EventIcon = eventConfig.Icon
          const isExpanded = expandedVersion === version.id
          const isLast = index === versions.length - 1

          return (
            <TimelineItem key={version.id} $isLast={isLast}>
              <TimelineLine $isLast={isLast} />
              <TimelineDot $color={eventConfig.color} $bg={eventConfig.bg}>
                <EventIcon style={{ fontSize: 16 }} />
              </TimelineDot>
              <TimelineContent>
                <VersionHeader onClick={() => toggleExpanded(version.id)}>
                  <VersionInfo>
                    <VersionLabel>
                      <EventChip $color={eventConfig.color} $bg={eventConfig.bg}>
                        {eventConfig.label}
                      </EventChip>
                      <VersionNumber>v{version.versionNumber}</VersionNumber>
                    </VersionLabel>
                    <VersionMeta>
                      {version.author ? (
                        <AuthorName>
                          {version.author.firstName} {version.author.lastName}
                        </AuthorName>
                      ) : version.externalEmail ? (
                        <AuthorName>{maskEmail(version.externalEmail)}</AuthorName>
                      ) : (
                        <AuthorName>{roleConfig.label}</AuthorName>
                      )}
                      <VersionTime>
                        {moment(version.createdAt).format('DD MMM YYYY, HH:mm')}
                      </VersionTime>
                    </VersionMeta>
                  </VersionInfo>
                  {version.changedFields?.length > 0 && (
                    <ExpandButton $expanded={isExpanded}>
                      <ExpandMoreIcon />
                    </ExpandButton>
                  )}
                </VersionHeader>

                {isExpanded && version.changedFields?.length > 0 && (
                  <ChangedFieldsList>
                    <ChangedFieldsTitle>Changed Fields</ChangedFieldsTitle>
                    {version.changedFields.map(fieldKey => (
                      <ChangedFieldChip key={fieldKey}>
                        {fieldKey}
                      </ChangedFieldChip>
                    ))}
                  </ChangedFieldsList>
                )}
              </TimelineContent>
            </TimelineItem>
          )
        })}
      </Timeline>
    </Container>
  )
}

export default FormVersionHistory

// --- Styled Components ---

const Container = styled.div`
  padding: 8px 0;
`

const LoadingText = styled.div`
  font-size: 13px;
  color: #94a3b8;
  text-align: center;
  padding: 24px;
`

const EmptyText = styled.div`
  font-size: 13px;
  color: #94a3b8;
  text-align: center;
  padding: 24px;
`

const Timeline = styled.div`
  position: relative;
`

const TimelineItem = styled.div`
  display: flex;
  gap: 14px;
  position: relative;
  padding-bottom: ${({ $isLast }) => $isLast ? '0' : '16px'};
`

const TimelineLine = styled.div`
  position: absolute;
  left: 15px;
  top: 32px;
  bottom: 0;
  width: 2px;
  background: ${({ $isLast }) => $isLast ? 'transparent' : '#e2e8f0'};
`

const TimelineDot = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${({ $bg }) => $bg};
  color: ${({ $color }) => $color};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  z-index: 1;
`

const TimelineContent = styled.div`
  flex: 1;
  min-width: 0;
`

const VersionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  padding: 6px 10px;
  border-radius: 8px;
  transition: background 0.1s;

  &:hover {
    background: #f8fafc;
  }
`

const VersionInfo = styled.div`
  flex: 1;
  min-width: 0;
`

const VersionLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 2px;
`

const EventChip = styled.span`
  display: inline-flex;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
  color: ${({ $color }) => $color};
  background: ${({ $bg }) => $bg};
`

const VersionNumber = styled.span`
  font-size: 11px;
  font-weight: 500;
  color: #94a3b8;
`

const VersionMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const AuthorName = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: #64748b;
`

const VersionTime = styled.span`
  font-size: 11px;
  color: #94a3b8;
`

const ExpandButton = styled.div`
  color: #94a3b8;
  transition: transform 0.2s;
  transform: ${({ $expanded }) => $expanded ? 'rotate(180deg)' : 'rotate(0)'};

  svg { font-size: 20px; }
`

const ChangedFieldsList = styled.div`
  padding: 10px 12px;
  margin-top: 6px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
`

const ChangedFieldsTitle = styled.div`
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #94a3b8;
  margin-bottom: 8px;
`

const ChangedFieldChip = styled.span`
  display: inline-flex;
  padding: 3px 10px;
  margin: 2px 4px 2px 0;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  color: #475569;
  background: #e2e8f0;
  font-family: monospace;
`
